/* Sprint 2R-4A payment-details extension — vognmand self-service.
   Shows masked current payment method + lets vognmand submit a change
   that requires EMA admin approval. */
import React from "react";
import { scApi } from "../lib/api";
import { Loader2, Banknote, AlertCircle, CheckCircle2, Hash, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((x) => x.msg).join(", ");
  return e?.message || "Fejl";
}

export default function VognmandPaymentDetailsPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await scApi().get("/sc/payment-details");
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-brand-text2"><Loader2 className="inline animate-spin" size={14}/> Indlæser betalingsoplysninger...</div>;

  return (
    <div className="space-y-4" data-testid="vognmand-payment-details-panel">
      <div>
        <div className="kicker">Betalingsoplysninger</div>
        <h1 className="font-display font-bold text-2xl text-brand-dark mt-1">Mine betalingsoplysninger</h1>
        <p className="text-sm text-brand-text2 mt-1">
          Ændringer skal godkendes af EMA Kurer, før de bruges til afregning. Nuværende godkendte detaljer bruges indtil ny godkendelse er på plads.
        </p>
      </div>

      {data?.approved ? (
        <div className="bg-emerald-50 border border-emerald-200 p-4" data-testid="payment-approved-card">
          <div className="flex items-center gap-2 text-xs font-bold uppercase text-emerald-900 mb-2">
            <CheckCircle2 size={14}/> Godkendt og aktiv
          </div>
          <PaymentRow pm={data.approved}/>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="payment-no-approved-card">
          <AlertCircle size={14} className="inline mr-1"/> Endnu ingen godkendte betalingsoplysninger. Gennemfør onboarding først.
        </div>
      )}

      {data?.pending && (
        <div className="bg-blue-50 border border-blue-200 p-4" data-testid="payment-pending-card">
          <div className="flex items-center gap-2 text-xs font-bold uppercase text-blue-900 mb-2">
            <Loader2 size={14} className="animate-spin"/> Afventer EMA admin-godkendelse
          </div>
          <PaymentRow pm={data.pending}/>
          <button onClick={async () => {
                    try {
                      await scApi().post("/sc/payment-details/cancel-change", {});
                      toast.success("Ændring annulleret");
                      load();
                    } catch (e) { toast.error(errMsg(e)); }
                  }}
                  className="mt-3 text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2"
                  data-testid="payment-cancel-pending">
            Annullér ventende ændring
          </button>
        </div>
      )}

      {!data?.pending && (
        editing ? (
          <PaymentChangeForm
            initialType={data?.approved?.type || "bank"}
            confirmationText={data?.confirmation_text}
            onSaved={() => { setEditing(false); load(); }}
            onCancel={() => setEditing(false)}/>
        ) : (
          <button onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-brand-red hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-2"
                  data-testid="payment-edit-open">
            <Banknote size={14}/> Skift betalingsoplysninger
          </button>
        )
      )}

      <div className="bg-brand-bg2 border border-brand p-3 text-xs text-brand-text2 space-y-1">
        <div className="flex items-center gap-1 font-bold text-brand-dark">
          <ShieldCheck size={12}/> DKK-only · ingen IBAN / MobilePay / PayPal
        </div>
        <div>Betalinger sker udelukkende i danske kroner til godkendt dansk reg.nr./kontonummer eller FI-kode tilknyttet jeres CVR.</div>
        <div>Chauffør-brugere har aldrig adgang til disse oplysninger.</div>
      </div>
    </div>
  );
}

function PaymentRow({ pm }) {
  if (!pm) return null;
  if (pm.type === "bank") {
    return (
      <div className="text-sm space-y-1" data-testid="payment-row-bank">
        <div><b>Type:</b> Dansk bankkonto</div>
        <div><b>Reg.nr.:</b> {pm.bank_reg_masked || "—"}</div>
        <div><b>Kontonummer:</b> {pm.bank_account_masked || "—"}</div>
        {pm.approved_at && <div className="text-xs text-brand-text2">Godkendt: {new Date(pm.approved_at).toLocaleString("da-DK")}</div>}
        {pm.submitted_at && !pm.approved_at && <div className="text-xs text-brand-text2">Indsendt: {new Date(pm.submitted_at).toLocaleString("da-DK")}</div>}
      </div>
    );
  }
  return (
    <div className="text-sm space-y-1" data-testid="payment-row-fi">
      <div><b>Type:</b> FI-betalingskode</div>
      <div><b>FI-kode:</b> {pm.fi_code_masked || "—"}</div>
      {pm.approved_at && <div className="text-xs text-brand-text2">Godkendt: {new Date(pm.approved_at).toLocaleString("da-DK")}</div>}
      {pm.submitted_at && !pm.approved_at && <div className="text-xs text-brand-text2">Indsendt: {new Date(pm.submitted_at).toLocaleString("da-DK")}</div>}
    </div>
  );
}

function PaymentChangeForm({ initialType, confirmationText, onSaved, onCancel }) {
  const [type, setType] = React.useState(initialType || "bank");
  const [bankReg, setBankReg] = React.useState("");
  const [bankAcc, setBankAcc] = React.useState("");
  const [fiCode, setFiCode] = React.useState("");
  const [confirmed, setConfirmed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!confirmed) return toast.error("Du skal bekræfte ejerskab af betalingsoplysningerne");
    setBusy(true);
    try {
      const body = {
        type,
        confirmation_text_accepted: true,
        bank_reg: type === "bank" ? bankReg : undefined,
        bank_account: type === "bank" ? bankAcc : undefined,
        fi_code: type === "fi" ? fiCode : undefined,
      };
      await scApi().post("/sc/payment-details/submit-change", body);
      toast.success("Ændring indsendt — afventer EMA admin-godkendelse");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-brand p-4 space-y-3" data-testid="payment-change-form">
      <div className="flex gap-2 mb-2">
        {["bank", "fi"].map((t) => (
          <button type="button" key={t} onClick={() => setType(t)}
                  data-testid={`payment-type-${t}`}
                  className={`px-3 py-1.5 text-xs font-bold uppercase border ${type === t ? "border-brand-red text-brand-red bg-white" : "border-brand text-brand-text2"}`}>
            {t === "bank" ? "Dansk bankkonto" : "FI-betalingskode"}
          </button>
        ))}
      </div>

      {type === "bank" && (
        <>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brand-text2 mb-1 block">Reg.nr. (4 cifre)</span>
            <input className="form-input w-full px-3 py-2 border border-brand"
                   value={bankReg} onChange={(e) => setBankReg(e.target.value)}
                   pattern="\d{4}" maxLength={4} required data-testid="payment-bank-reg"/>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brand-text2 mb-1 block">Kontonummer</span>
            <input className="form-input w-full px-3 py-2 border border-brand"
                   value={bankAcc} onChange={(e) => setBankAcc(e.target.value)}
                   pattern="\d{4,20}" maxLength={20} required data-testid="payment-bank-account"/>
          </label>
        </>
      )}

      {type === "fi" && (
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-brand-text2 mb-1 block">FI-kode / kreditor-reference</span>
          <input className="form-input w-full px-3 py-2 border border-brand"
                 value={fiCode} onChange={(e) => setFiCode(e.target.value)}
                 pattern="\d{8,20}" maxLength={20} required data-testid="payment-fi-code"/>
        </label>
      )}

      <label className="flex items-start gap-2 cursor-pointer text-sm mt-3">
        <input type="checkbox" className="mt-1" checked={confirmed}
               onChange={(e) => setConfirmed(e.target.checked)}
               data-testid="payment-confirm-ownership"/>
        <span>{confirmationText}</span>
      </label>

      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <button type="submit" disabled={busy}
                className="px-4 py-2 bg-brand-red hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                data-testid="payment-submit-change">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : null}
          Indsend til EMA godkendelse
        </button>
        <button type="button" onClick={onCancel}
                className="px-4 py-2 bg-white border border-brand text-sm">
          Annullér
        </button>
      </div>
    </form>
  );
}
